(() => {
  if (customElements.get('hf-care-collection-upsell')) return;
  const transactions = new Map();
  class CareCollectionUpsell extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.state = transactions.get(this.dataset.sectionId) || { busy: false, uncertain: false, quantities: {} };
      transactions.set(this.dataset.sectionId, this.state);
      this.state.host = this;
      const options = { signal: this.controller.signal };
      this.addEventListener('change', event => {
        if (event.target.matches('[data-care-choice]')) this.select(event.target.value);
      }, options);
      this.addEventListener('input', event => {
        if (event.target.matches('[name="quantity"]') && this.activePanel?.contains(event.target)) this.state.quantities[this.activePanel.dataset.productId] = event.target.value;
      }, options);
      this.addEventListener('submit', event => this.submit(event), { ...options, capture: true });
      const selected = Array.from(this.querySelectorAll('[data-care-choice]')).find(choice => choice.value === this.state.selected) || this.querySelector('[data-care-choice]:checked');
      if (selected) selected.checked = true;
      if (selected) this.select(selected.value, true);
      this.setBusy(this.state.busy || this.state.uncertain);
      if (this.state.uncertain) this.message(this.dataset.unknown, true);
    }
    disconnectedCallback() { this.controller?.abort(); }
    select(id, initial = false) {
      if ((this.state.busy || this.state.uncertain) && !initial) return;
      this.state.selected = id;
      this.querySelectorAll('[data-care-panel]').forEach(panel => {
        const active = panel.dataset.productId === id;
        panel.hidden = !active;
        panel.inert = !active;
        panel.querySelector('[data-care-form-controls]').disabled = !active || this.state.busy || this.state.uncertain;
        if (active) {
          this.activePanel = panel;
          const quantity = panel.querySelector('[name="quantity"]');
          const remembered = Number(this.state.quantities[id]);
          if (quantity && Number.isSafeInteger(remembered) && remembered >= Number(quantity.min || 1)) {
            quantity.value = String(quantity.max ? Math.min(remembered, Number(quantity.max)) : remembered);
          }
          this.querySelector('[data-care-cloth-slot]').replaceChildren(panel.querySelector('template[data-care-cloth]').content.cloneNode(true));
        }
      });
      this.message('');
    }
    message(text, recovery = false) {
      const host = this.state?.host || this;
      host.querySelector('[data-care-status]').textContent = text;
      host.querySelector('[data-care-recovery]').hidden = !recovery;
    }
    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      if (this.activePanel) this.activePanel.querySelector('[data-care-form-controls]').disabled = busy;
      this.querySelectorAll('[data-care-choice]').forEach(choice => { choice.disabled = busy; });
      this.querySelectorAll('[name="quantity"]').forEach(input => { input.readOnly = busy; });
      this.querySelectorAll('button[type="submit"]').forEach(button => {
        if (busy) button.setAttribute('aria-busy', 'true');
        else button.removeAttribute('aria-busy');
      });
    }
    async readCart(root) {
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('cart_read_failed');
      return response.json();
    }
    async submit(event) {
      const form = event.target;
      if (!form.matches('product-form form[action*="/cart/add"]')) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const panel = form.closest('[data-care-panel]');
      const fieldset = panel?.querySelector('[data-care-form-controls]');
      const submitter = event.submitter || form.querySelector('button[type="submit"]');
      if (this.state.busy || this.state.uncertain || panel !== this.activePanel || panel.hidden || panel.inert || fieldset.disabled || !submitter || submitter.disabled) return;
      if (!form.checkValidity()) { form.reportValidity(); return; }
      const payload = new FormData(form);
      const quantity = Number(payload.get('quantity'));
      if (payload.get('id') !== panel.dataset.variantId || !Number.isSafeInteger(quantity) || quantity < 1 || payload.has('items[]')) return;
      this.state.busy = true;
      this.setBusy(true);
      const root = window.Shopify?.routes?.root || '/';
      let attempted = false;
      let accepted = false;
      const doc = document.documentElement;
      doc.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
      try {
        await this.readCart(root);
        const sections = [];
        doc.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
        payload.set('sections', [...new Set(sections)].slice(0, 5).join(','));
        payload.set('sections_url', location.pathname + location.search);
        payload.set('on_success', 'force_open_drawer');
        attempted = true;
        const response = await fetch(`${root}cart/add.js`, { method: 'POST', body: payload, credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' }, signal: AbortSignal.timeout(20000) });
        const added = await response.json();
        if (!response.ok) {
          if (response.status >= 500) throw new Error('uncertain_server_result');
          this.message(added.description || this.dataset.error);
          await this.readCart(root);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          return;
        }
        accepted = true;
        const cart = await this.readCart(root);
        let rendered = added.sections || {};
        if (sections.some(key => !rendered[key])) {
          const query = new URLSearchParams({ sections: [...new Set(sections)].slice(0, 5).join(',') });
          const renderResponse = await fetch(`${location.pathname}?${query}`, { credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
          if (renderResponse.ok) rendered = await renderResponse.json();
        }
        if (sections.some(key => typeof rendered[key] !== 'string' || !rendered[key].includes('cart-drawer'))) {
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          this.message(this.dataset.success, true);
          return;
        }
        cart.sections = rendered;
        form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: { items: added.items || [added], cart, onSuccessDo: 'force_open_drawer' } }));
        doc.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', onSuccessDo: 'force_open_drawer', cart } }));
        this.message(this.dataset.success);
      } catch {
        this.state.uncertain = attempted && !accepted;
        try {
          await this.readCart(root);
          document.dispatchEvent(new CustomEvent('cart:refresh'));
        } catch { }
        this.message(accepted ? this.dataset.success : attempted ? this.dataset.unknown : this.dataset.error, attempted);
      } finally {
        this.state.busy = false;
        (this.state.host || this).setBusy(this.state.uncertain);
        doc.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
      }
    }
  }
  customElements.define('hf-care-collection-upsell', CareCollectionUpsell);
})();
